import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

const ADMIN_TOKEN_KEY = "voice_ideias_admin_token";
const ADMIN_KEY = "voice_ideias_admin";

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get auth headers
  const getAuthHeaders = useCallback(() => {
    const storedToken = token || localStorage.getItem(ADMIN_TOKEN_KEY);
    return storedToken ? { Authorization: `Bearer ${storedToken}` } : {};
  }, [token]);

  // Load admin from localStorage on mount
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const storedToken = localStorage.getItem(ADMIN_TOKEN_KEY);
        const storedAdmin = localStorage.getItem(ADMIN_KEY);

        if (storedToken && storedAdmin) {
          const response = await axios.get(`${API}/verify`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });

          if (response.data.valid) {
            setToken(storedToken);
            setAdmin(response.data.admin);
          } else {
            localStorage.removeItem(ADMIN_TOKEN_KEY);
            localStorage.removeItem(ADMIN_KEY);
          }
        }
      } catch (error) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_KEY);
      } finally {
        setLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/login`, {
      email,
      password,
    });

    const { access_token, admin: adminData } = response.data;

    localStorage.setItem(ADMIN_TOKEN_KEY, access_token);
    localStorage.setItem(ADMIN_KEY, JSON.stringify(adminData));

    setToken(access_token);
    setAdmin(adminData);

    return adminData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
    setToken(null);
    setAdmin(null);
  }, []);

  const value = {
    admin,
    token,
    loading,
    isAuthenticated: !!admin,
    login,
    logout,
    getAuthHeaders,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export default AdminAuthContext;
